import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";

// M-Q5: First-login sample data prompt.
// Shown once per user (platform.profiles.has_seen_sample_prompt = false).
// On confirm → invokes manna-seed-samples edge function.
// On skip / confirm → marks prompt seen via manna_mark_sample_prompt_seen().

function useHasSeenPrompt() {
  return useQuery({
    queryKey: ["sample-data-prompt"],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("manna_my_profile");
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return (row?.has_seen_sample_prompt as boolean) ?? true;
    },
    staleTime: Infinity,
  });
}

function useMarkSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.rpc as any)("manna_mark_sample_prompt_seen");
      if (error) throw error;
    },
    onSuccess: () => {
      qc.setQueryData(["sample-data-prompt"], true);
    },
  });
}

function useSeedSamples() {
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.functions.invoke("manna-seed-samples");
      if (error) throw error;
    },
  });
}

export function SampleDataPrompt() {
  const { data: hasSeen, isLoading } = useHasSeenPrompt();
  const markSeen = useMarkSeen();
  const seedSamples = useSeedSamples();
  const [seedError, setSeedError] = useState<string | null>(null);

  // Don't show until we know the state.
  if (isLoading || hasSeen) return null;

  const open = !hasSeen;

  const handleSeed = async () => {
    setSeedError(null);
    try {
      await seedSamples.mutateAsync();
      await markSeen.mutateAsync();
    } catch (e) {
      setSeedError(e instanceof Error ? e.message : "Seeding failed — try again.");
    }
  };

  const handleSkip = async () => {
    await markSeen.mutateAsync();
  };

  const busy = seedSamples.isPending || markSeen.isPending;

  return (
    <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle>Populate sample data?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Manna can pre-load a set of sample contacts, organizations, deals, and activities to help
          you explore the platform before entering real data.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sample data is labelled and can be removed any time from{" "}
          <strong>Admin → Remove sample data</strong>.
        </Typography>
        {seedError && (
          <Typography variant="body2" color="error" mt={1}>
            {seedError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSkip} disabled={busy}>
          Skip
        </Button>
        <Button
          variant="contained"
          onClick={handleSeed}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
        >
          {busy ? "Populating…" : "Populate sample data"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
